<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useDetectKeyboardLayout } from 'dashboard/composables/useDetectKeyboardLayout';
import { useKeyboardShortcuts } from 'dashboard/composables/useKeyboardShortcuts';
import {
  SHORTCUT_MODAL_ROWS,
  SHORTCUT_ACTIONS,
  KEYS,
  keysToDisplayTokens,
  keyboardEventToKeys,
  keyboardEventToEditorTrigger,
  isEditorTriggerAction,
  actionTitlePath,
} from './constants';
import {
  LAYOUT_QWERTZ,
  keysToModifyInQWERTZ,
} from 'shared/helpers/KeyboardHelpers';
import Hotkey from 'dashboard/components/base/Hotkey.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({ show: Boolean });
defineEmits(['close']);

const { t } = useI18n();
/** 当前检测到的键盘布局（用于 QWERTZ 展示 Shift） */
const currentLayout = ref(null);
/** 正在录制的动作 id；非空表示处于「按下新组合」状态 */
const recordingAction = ref(null);

const { getKeySet, setKeySet, restoreDefaults, resolvedShortcuts } =
  useKeyboardShortcuts();

/** 行标题文案：根据 SHORTCUT_MODAL_ROWS.label 取 i18n */
const title = computed(
  () => item => t(`KEYBOARD_SHORTCUTS.TITLE.${item.label}`)
);

/**
 * 弹窗主体行：附带每个 action 当前 keys / 展示 tokens，
 * 依赖 resolvedShortcuts，用户改键后自动刷新。
 */
const modalRows = computed(() => {
  void resolvedShortcuts.value;
  return SHORTCUT_MODAL_ROWS.map(row => ({
    ...row,
    chips: row.actions.map(action => ({
      action,
      keys: getKeySet(action),
      tokens: keysToDisplayTokens(getKeySet(action)),
    })),
  }));
});

/** 「查看全部快捷键」当前绑定字符串 */
const toggleModalKeys = computed(() => {
  void resolvedShortcuts.value;
  return getKeySet(SHORTCUT_ACTIONS.TOGGLE_MODAL);
});

/** 「查看全部快捷键」展示芯片 */
const toggleModalTokens = computed(() =>
  keysToDisplayTokens(toggleModalKeys.value)
);

/**
 * QWERTZ 布局下，部分组合需要额外展示 Shift。
 * @returns {(keys: string) => boolean}
 */
const needsShiftKey = computed(() => keys => {
  if (!keys || currentLayout.value !== LAYOUT_QWERTZ) return false;
  return keysToModifyInQWERTZ.has(keys);
});

/** 退出录制态 */
const stopRecording = () => {
  recordingAction.value = null;
};

/**
 * 进入某动作的录制态，等待下一次有效按键组合。
 * @param {string} action
 */
const startRecording = action => {
  recordingAction.value = action;
};

/** 一键清空用户覆盖并提示成功 */
const onRestoreDefaults = () => {
  stopRecording();
  restoreDefaults();
  useAlert(t('KEYBOARD_SHORTCUTS.RESTORE_SUCCESS'));
};

/**
 * 录制态的捕获阶段 keydown：
 * - Esc：取消录制
 * - 编辑器触发类动作：写入单个输入字符
 * - 其余动作：写入 tinykeys 组合；冲突则 toast 并拒绝
 * - 纯修饰键 / 非法键：忽略，继续等待
 */
const handleRecordingKeydown = event => {
  if (!recordingAction.value || !props.show) return;

  event.preventDefault();
  event.stopPropagation();

  if (event.key === 'Escape') {
    stopRecording();
    return;
  }

  const isEditorTrigger = isEditorTriggerAction(recordingAction.value);
  const keys = isEditorTrigger
    ? keyboardEventToEditorTrigger(event)
    : keyboardEventToKeys(event);

  if (!keys) {
    if (isEditorTrigger && event.key.length === 1) {
      useAlert(t('KEYBOARD_SHORTCUTS.INVALID_EDITOR_TRIGGER'));
      stopRecording();
    }
    return;
  }

  const result = setKeySet(recordingAction.value, keys);
  if (!result.ok) {
    if (result.reason === 'conflict') {
      const { path } = actionTitlePath(result.conflict);
      useAlert(
        t('KEYBOARD_SHORTCUTS.CONFLICT', {
          shortcut: t(path),
        })
      );
    }
    stopRecording();
    return;
  }

  stopRecording();
};

watch(
  () => props.show,
  isOpen => {
    if (!isOpen) stopRecording();
  }
);

onMounted(async () => {
  currentLayout.value = await useDetectKeyboardLayout();
  document.addEventListener('keydown', handleRecordingKeydown, true);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleRecordingKeydown, true);
});
</script>

<template>
  <woot-modal :show="show" size="medium" :on-close="() => $emit('close')">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS')"
      />

      <p class="px-8 text-sm text-n-slate-11">
        {{ $t('KEYBOARD_SHORTCUTS.EDIT_HINT') }}
      </p>

      <div class="grid grid-cols-2 px-8 pt-0 pb-4 mt-4 gap-x-5 gap-y-3">
        <div class="flex justify-between items-center min-w-[25rem]">
          <h5 class="text-sm text-n-slate-12">
            {{ $t('KEYBOARD_SHORTCUTS.TOGGLE_MODAL') }}
          </h5>
          <button
            type="button"
            class="flex items-center gap-2 mb-1 ml-2 rounded-md px-1 py-0.5 transition-colors"
            :class="
              recordingAction === SHORTCUT_ACTIONS.TOGGLE_MODAL
                ? 'bg-n-brand/10 ring-1 ring-n-brand'
                : 'hover:bg-n-alpha-2'
            "
            :aria-label="$t('KEYBOARD_SHORTCUTS.RECORDING')"
            @click="startRecording(SHORTCUT_ACTIONS.TOGGLE_MODAL)"
          >
            <template v-if="recordingAction === SHORTCUT_ACTIONS.TOGGLE_MODAL">
              <span class="text-xs text-n-brand font-medium px-1">
                {{ $t('KEYBOARD_SHORTCUTS.RECORDING') }}
              </span>
            </template>
            <template v-else>
              <template v-if="needsShiftKey(toggleModalKeys)">
                <Hotkey
                  custom-class="min-h-[28px] min-w-[36px] key py-2 px-2.5 font-semibold text-xs text-n-slate-12 bg-n-slate-4 dark:bg-n-slate-2 shadow border-b-2 rtl:border-l-2 ltr:border-r-2 border-n-strong"
                >
                  {{ KEYS.SHIFT }}
                </Hotkey>
              </template>
              <Hotkey
                v-for="(key, index) in toggleModalTokens"
                :key="`toggle-${index}`"
                custom-class="min-h-[28px] min-w-[36px] key normal-case py-2 px-2.5 font-semibold text-xs text-n-slate-12 bg-n-slate-4 dark:bg-n-slate-2 shadow border-b-2 rtl:border-l-2 ltr:border-r-2 border-n-strong"
              >
                {{ key }}
              </Hotkey>
            </template>
          </button>
        </div>
      </div>

      <div class="grid grid-cols-2 px-8 pt-0 pb-4 gap-x-5 gap-y-3">
        <div
          v-for="shortcut in modalRows"
          :key="shortcut.id"
          class="flex justify-between items-center min-w-[25rem]"
        >
          <h5 class="text-sm text-n-slate-12 min-w-[36px]">
            {{ title(shortcut) }}
          </h5>
          <div class="flex items-center gap-2 mb-1 ml-2">
            <template
              v-for="(chip, chipIndex) in shortcut.chips"
              :key="chip.action"
            >
              <span
                v-if="chipIndex > 0"
                class="flex items-center text-sm font-semibold text-n-slate-12"
              >
                {{ KEYS.SLASH }}
              </span>
              <button
                type="button"
                class="flex items-center gap-2 rounded-md px-1 py-0.5 transition-colors"
                :class="
                  recordingAction === chip.action
                    ? 'bg-n-brand/10 ring-1 ring-n-brand'
                    : 'hover:bg-n-alpha-2'
                "
                :aria-label="$t('KEYBOARD_SHORTCUTS.RECORDING')"
                @click="startRecording(chip.action)"
              >
                <template v-if="recordingAction === chip.action">
                  <span class="text-xs text-n-brand font-medium px-1">
                    {{ $t('KEYBOARD_SHORTCUTS.RECORDING') }}
                  </span>
                </template>
                <template v-else>
                  <template v-if="needsShiftKey(chip.keys)">
                    <Hotkey
                      custom-class="min-h-[28px] min-w-[36px] key py-2 px-2.5 font-semibold text-xs text-n-slate-12 bg-n-slate-4 dark:bg-n-slate-2 shadow border-b-2 rtl:border-l-2 ltr:border-r-2 border-n-strong"
                    >
                      {{ KEYS.SHIFT }}
                    </Hotkey>
                  </template>
                  <Hotkey
                    v-for="(key, index) in chip.tokens"
                    :key="`${chip.action}-${index}`"
                    custom-class="min-h-[28px] min-w-[36px] key normal-case py-2 px-2.5 font-semibold text-xs text-n-slate-12 bg-n-slate-4 dark:bg-n-slate-2 shadow border-b-2 rtl:border-l-2 ltr:border-r-2 border-n-strong"
                  >
                    {{ key }}
                  </Hotkey>
                </template>
              </button>
            </template>
          </div>
        </div>
      </div>

      <div class="flex justify-end px-8 pb-6">
        <Button
          faded
          slate
          type="button"
          :label="$t('KEYBOARD_SHORTCUTS.RESTORE_DEFAULTS')"
          @click="onRestoreDefaults"
        />
      </div>
    </div>
  </woot-modal>
</template>
